/**
 * The Majestic — GalleryTabs override (photo gallery page).
 *
 * Customer request: the mobile gallery presented every photo through a
 * single-image swipe carousel (dozens of slides deep), which was
 * unintuitive. Switch mobile over to the same click-to-enlarge grid the
 * tablet/desktop breakpoints already use.
 *
 * The mobile carousel is baked into the parent's shared `GalleryRow`
 * (`@vibesites/theme-via6/components/_galleryShared`) — it renders the
 * multi-column grid at `sm+` and swaps in `MobileGalleryCarousel` below
 * `sm`. That split lives inside a shared helper we can't edit, so we
 * fork the registered `GalleryTabs` slot and render a local
 * `GalleryGrid` (grid at every breakpoint, no carousel) in place of
 * `GalleryRow`. The lightbox — reused verbatim from the shared helper —
 * still provides the "click to enlarge" view with arrows / swipe /
 * keyboard nav, so tapping any tile on mobile opens the full photo.
 *
 * Everything else (tab pills, headings-removed panels, square 4:3 tiles,
 * primary-color section background, the schema) is copied unchanged from
 * the parent so the section keeps drifting as little as possible.
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from '@vibesites/theme-via6/components/_galleryShared';

type Tab = 'all' | 'community' | 'apartment';

/**
 * Responsive click-to-enlarge grid. Unlike the shared `GalleryRow`, the
 * grid is visible at every breakpoint (mobile included) and there is no
 * carousel — tapping a tile calls `onSelect`, which opens the lightbox.
 * Tile classes match the shared grid so the section's scoped CSS (square
 * 4:3 tiles, 4-up at md) applies unchanged.
 */
function GalleryGrid({
  images,
  onSelect,
}: {
  images: GalleryImage[];
  onSelect: (index: number) => void;
}) {
  if (images.length === 0) return null;
  return (
    <div className="gallery-row">
      <div className="gallery-row__grid grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => (
          <div
            key={index}
            className="aspect-video overflow-hidden group cursor-pointer gallery-item"
            role="button"
            tabIndex={0}
            aria-label={`Enlarge image ${index + 1}`}
            onClick={() => onSelect(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(index);
              }
            }}
          >
            <VibesitesImage
              url={img.image}
              alt={img.alt_text || img.title || ''}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              sizes="(min-width: 768px) 25vw, 50vw"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function GalleryTabs() {
  const allTabLabel = useContentToken('gallerytabs.all_tab_label');
  const communityTabLabel = useContentToken('gallerytabs.community_tab_label');
  const apartmentTabLabel = useContentToken('gallerytabs.apartment_tab_label');

  const communityImages = useCollectionBinding<GalleryImage>(
    'gallerytabs.community_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Community' },
    },
  );
  const apartmentImages = useCollectionBinding<GalleryImage>(
    'gallerytabs.apartment_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
    },
  );

  // Concatenate so "All" feels like the union of both feeds. Memoized to
  // keep the lightbox's image identity stable across re-renders that
  // don't actually change the underlying data.
  const allImages = useMemo<GalleryImage[]>(
    () => [...communityImages, ...apartmentImages],
    [communityImages, apartmentImages],
  );

  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const lightboxImages =
    activeTab === 'all'
      ? allImages
      : activeTab === 'community'
        ? communityImages
        : apartmentImages;

  const switchTo = (tab: Tab) => {
    setSelectedIndex(null);
    setActiveTab(tab);
  };

  // Tab buttons are inlined (rather than emitted through a helper) so the
  // EditableToken `path` is a literal string the schema linter can see.
  const allActive = activeTab === 'all';
  const communityActive = activeTab === 'community';
  const apartmentActive = activeTab === 'apartment';

  return (
    <section className="gallery-tabs-section">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <div
          className="gallery-tabs"
          role="tablist"
          aria-label="Photo gallery categories"
        >
          <button
            type="button"
            role="tab"
            aria-selected={allActive}
            tabIndex={allActive ? 0 : -1}
            className={`gallery-tabs__tab gallery-tabs__tab--all${allActive ? ' is-active' : ''}`}
            onClick={() => switchTo('all')}
          >
            <EditableToken path="gallerytabs.all_tab_label" as="span">
              {allTabLabel}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={apartmentActive}
            tabIndex={apartmentActive ? 0 : -1}
            className={`gallery-tabs__tab${apartmentActive ? ' is-active' : ''}`}
            onClick={() => switchTo('apartment')}
          >
            <EditableToken path="gallerytabs.apartment_tab_label" as="span">
              {apartmentTabLabel}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={communityActive}
            tabIndex={communityActive ? 0 : -1}
            className={`gallery-tabs__tab${communityActive ? ' is-active' : ''}`}
            onClick={() => switchTo('community')}
          >
            <EditableToken path="gallerytabs.community_tab_label" as="span">
              {communityTabLabel}
            </EditableToken>
          </button>
        </div>

        {/*
          All three tab panels stay in the DOM (with `display: none` on the
          inactive ones) so the lightbox can switch panels without
          remounting the grid. Panel headings have been removed — the
          active pill above acts as the visible label.
        */}
        <div
          role="tabpanel"
          aria-label="All photos"
          hidden={activeTab !== 'all'}
          style={activeTab === 'all' ? undefined : { display: 'none' }}
        >
          {allImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryGrid images={allImages} onSelect={setSelectedIndex} />
          )}
        </div>

        <div
          role="tabpanel"
          aria-label="Community photos"
          hidden={activeTab !== 'community'}
          style={activeTab === 'community' ? undefined : { display: 'none' }}
        >
          {communityImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryGrid images={communityImages} onSelect={setSelectedIndex} />
          )}
        </div>

        <div
          role="tabpanel"
          aria-label="Apartment photos"
          hidden={activeTab !== 'apartment'}
          style={activeTab === 'apartment' ? undefined : { display: 'none' }}
        >
          {apartmentImages.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-body text-lg gallery-tabs__empty">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <GalleryGrid images={apartmentImages} onSelect={setSelectedIndex} />
          )}
        </div>
      </div>

      <GalleryLightbox
        images={lightboxImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
      <style>{`
        .gallery-tabs-section {
          background: var(--vibe-color-primary);
          color: #fff;
        }

        .gallery-tabs-section .gallery-tabs__empty {
          color: rgba(255, 255, 255, 0.85);
        }

        /* ============================================================
           Tabs styled as CTA pills, matching the rounded-full secondary
           pills used by PropertyOverview / AmenitiesGrid / PetCallout.
             - Inactive: outlined white pill on the primary background.
             - Active:   filled secondary pill with text-on-primary.
             - Hover:    flip to white fill with secondary text.
           No bottom border / underline — the pill is the affordance.
           ============================================================ */
        .gallery-tabs {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.625rem;
          margin-bottom: 2rem;
        }
        .gallery-tabs__tab {
          display: inline-block;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.625rem 1.75rem;
          border-radius: 9999px;
          background: transparent;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.85);
          cursor: pointer;
          transition: background-color 0.15s ease-out, color 0.15s ease-out,
            border-color 0.15s ease-out;
        }
        .gallery-tabs__tab:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .gallery-tabs__tab.is-active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-secondary);
        }
        .gallery-tabs__tab.is-active:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .gallery-tabs__tab:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 2px;
        }
        /*
          On narrow phones the three pills would otherwise wrap onto two
          rows. Shrink the type and horizontal padding (and tighten the
          row gap) so All / Apartment / Community sit on a single line.
        */
        @media (max-width: 480px) {
          .gallery-tabs {
            gap: 0.375rem;
            flex-wrap: nowrap;
          }
          .gallery-tabs__tab {
            font-size: 0.6875rem;
            letter-spacing: 0.12em;
            padding: 0.5rem 0.875rem;
          }
        }
        /* The All pill keeps a solid white border in every state so it
           reads as the "reset" / default filter even when active. */
        .gallery-tabs__tab--all,
        .gallery-tabs__tab--all:hover,
        .gallery-tabs__tab--all.is-active,
        .gallery-tabs__tab--all.is-active:hover {
          border-color: #fff;
        }

        /*
          Override the shared 16:9 tiles to square. The shared file applies
          the aspect-video utility (Tailwind: aspect-ratio: 16 / 9) to each
          grid cell; we win on specificity by scoping under
          .gallery-tabs-section and using !important so this stays robust to
          Tailwind utility ordering.
        */
        .gallery-tabs-section .gallery-row__grid > .aspect-video {
          aspect-ratio: 4 / 3 !important;
        }

        /*
          Widen the tablet/desktop grid from the shared 2/3-up layout to
          4 images wide at the md breakpoint.
        */
        @media (min-width: 768px) {
          .gallery-tabs-section .gallery-row__grid {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          }
        }

        /*
          Mobile (< sm): the parent theme's shared GalleryRow swaps the grid
          for a single-image swipe carousel here. We render the grid at every
          breakpoint instead (see GalleryGrid above), so on phones the gallery
          is a 2-up thumbnail grid — tap any tile to open the click-to-enlarge
          lightbox. Re-establish a comfortable gutter + gap (the shared mobile
          stylesheet zeroes both for its edge-to-edge carousel).
        */
        @media (max-width: 639px) {
          .gallery-tabs-section .gallery-constrained {
            width: 100vw;
            max-width: 100vw;
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          .gallery-tabs-section .gallery-row__grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 0.5rem !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryTabs',
  displayName: 'Gallery (Tabs)',
  tokens: {
    'gallerytabs.all_tab_label': {
      label: 'All Tab Label',
      type: 'text',
      defaultValue: 'All',
    },
    'gallerytabs.community_tab_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community',
    },
    'gallerytabs.apartment_tab_label': {
      label: 'Apartment Tab Label',
      type: 'text',
      defaultValue: 'Apartment',
    },
  },
  collectionBindings: {
    'gallerytabs.community_images': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Community" appear in the Community tab (and in the All tab).',
    },
    'gallerytabs.apartment_images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Apartment" appear in the Apartment tab (and in the All tab).',
    },
  },
};

(GalleryTabs as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryTabs;
